import io from "socket.io-client";
import discovery from "../discovery";

let socket = null;
let subscribedChannelName = null;
let boundMessageHandler = null;

function normalizeSessionInformation(sessionInformation) {
  if (!sessionInformation) {
    return null;
  }

  if (typeof sessionInformation === "string") {
    try {
      return JSON.parse(sessionInformation);
    } catch (error) {
      console.warn("Failed to parse session information for websocket", error);
      return null;
    }
  }

  return sessionInformation;
}

function safeParseMessageContent(messageContent) {
  if (!messageContent) {
    return {};
  }

  try {
    return JSON.parse(messageContent);
  } catch (error) {
    console.warn("Failed to parse websocket message_content", error);
    return {};
  }
}

function getActiveCompanyId(getSessionInformation) {
  try {
    const sessionInformation = normalizeSessionInformation(getSessionInformation ? getSessionInformation() : null);
    if (!sessionInformation || !sessionInformation.company) {
      return sessionInformation && sessionInformation.companyId ? sessionInformation.companyId : null;
    }

    return sessionInformation.company.id || null;
  } catch (error) {
    console.warn("Failed to resolve active company for websocket filtering", error);
    return null;
  }
}

function shouldProcessEvent(payload, getSessionInformation) {
  const activeCompanyId = getActiveCompanyId(getSessionInformation);
  if (!payload.company_id || !activeCompanyId) {
    return true;
  }

  return String(payload.company_id) === String(activeCompanyId);
}

function buildPushEnvelope(event, payload) {
  const isProfilePictureUpdate = event.message_type === "profile-picture-update";

  return JSON.stringify({
    data: {
      type: isProfilePictureUpdate ? "profile-picture-update" : "sync",
      payload: payload.payload || event.message_type || "",
    },
  });
}

function buildVueDetail(event, payload) {
  const nestedPayload = payload && typeof payload.payload === "string"
    ? safeParseMessageContent(payload.payload)
    : (payload ? payload.payload : null);

  return {
    event,
    messageType: event.message_type || "",
    payload,
    normalizedPayload: nestedPayload || payload || {},
  };
}

export function createWebSocketService(getSessionInformation, onReconnectRefresh) {
  function emitAsNativePush(event) {
    const payload = safeParseMessageContent(event.message_content);
    if (!shouldProcessEvent(payload, getSessionInformation)) {
      return;
    }

    if (typeof window.dispatchEvent === "function" && typeof window.CustomEvent === "function") {
      window.dispatchEvent(new window.CustomEvent("tweem-channel-message", {
        detail: buildVueDetail(event, payload),
      }));
    }

    if (typeof window.gwt_onNativeDeviceEvent !== "function") {
      console.warn("gwt_onNativeDeviceEvent is not available");
      return;
    }

    window.gwt_onNativeDeviceEvent(`NE_PUSH:${buildPushEnvelope(event, payload)}`);
  }

  function connect(username) {
    if (!username) {
      return;
    }

    const channelUrl = discovery.socketio_channel_url;
    const environment = discovery.tweem_environment;
    if (!channelUrl || !environment) {
      console.warn("socketio-channel discovery settings are missing");
      return;
    }

    const channelName = `tweem-${environment}-${username}`;

    if (socket && subscribedChannelName === channelName) {
      return;
    }

    disconnect();

    socket = io(channelUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      query: {
        token: window.kc_token || "",
        targetApp: "worktime-admin",
        targetEnv: environment,
      },
    });
    subscribedChannelName = channelName;
    boundMessageHandler = emitAsNativePush;

    socket.on("connect", () => {
      if (subscribedChannelName) {
        socket.emit("join-channel", subscribedChannelName);
      }
    });

    socket.on("reconnect", () => {
      if (subscribedChannelName) {
        socket.emit("join-channel", subscribedChannelName);
      }

      if (typeof window.gwt_onNativeDeviceEvent === "function") {
        window.gwt_onNativeDeviceEvent("NE_AWAKE:301");
      }

      if (typeof onReconnectRefresh === "function") {
        onReconnectRefresh();
      }
    });

    socket.on(channelName, boundMessageHandler);
  }

  function disconnect() {
    if (!socket) {
      subscribedChannelName = null;
      boundMessageHandler = null;
      return;
    }

    if (subscribedChannelName) {
      if (boundMessageHandler) {
        socket.off(subscribedChannelName, boundMessageHandler);
      }
      socket.emit("leave-channel", subscribedChannelName);
    }

    socket.disconnect();
    socket = null;
    subscribedChannelName = null;
    boundMessageHandler = null;
  }

  return { connect, disconnect };
}
